import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, Library, Upload, FileText, Edit, Loader2, Sparkles, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import Sidebar from "@/components/Sidebar";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MultiBookSeries = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [completedBooks, setCompletedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showNewSeries, setShowNewSeries] = useState(false);
  const [showSeriesDetails, setShowSeriesDetails] = useState(false);
  const [showGenerateBook, setShowGenerateBook] = useState(false);
  
  // Selected series
  const [selectedSeries, setSelectedSeries] = useState(null);
  
  // Series creation state
  const [creationStep, setCreationStep] = useState(1); // 1: basic info, 2: book number, 3: upload/select, 4: review context, 5: story bible
  const [seriesFormData, setSeriesFormData] = useState({
    name: "",
    description: "",
    sourceType: "upload", // "upload" or "existing"
    targetBookNumber: 2 // Which book in series they're creating (2-5)
  });
  
  // File upload state - now an object with book numbers as keys
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [selectedExistingBooks, setSelectedExistingBooks] = useState([]);
  
  // Context extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractedContext, setExtractedContext] = useState(null);
  const [refreshingContext, setRefreshingContext] = useState(false);
  
  // New book generation state
  const [newBookData, setNewBookData] = useState({
    title: "",
    premise: "",
    additional_instructions: "",
    series_order: 0,
    total_planned_books: 5,  // NEW: Total books planned in series
    time_jump: "",
    genre: "fiction",
    total_chapters: 15,
    words_per_chapter: 3300,  // Default to 3300 words per chapter
    is_prequel: false,  // NEW: Flag for prequel books
    imported_chapter_outline: null,  // NEW: User-provided chapter outline
    character_roles: {},  // NEW: Character role assignments { "Marcus": "main", "Elena": "main", "Leo": "supporting" }
    new_main_characters: []  // NEW: Additional main characters for this book
  });
  const [generating, setGenerating] = useState(false);
  
  // Story Bible state
  const [showStoryBible, setShowStoryBible] = useState(false);
  const [storyBible, setStoryBible] = useState({
    core_characters: [],
    established_facts: [],
    writing_rules: [],
    forbidden: [],
    plot_threads: []
  });
  const [savingBible, setSavingBible] = useState(false);
  const [newCharacter, setNewCharacter] = useState({ name: "", role: "", description: "", relationships: "" });
  const [newFact, setNewFact] = useState("");
  const [newRule, setNewRule] = useState("");
  const [newForbidden, setNewForbidden] = useState("");

  // Server-side draft saving (persists across sessions/restarts)
  const [draftsLoaded, setDraftsLoaded] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Load saved drafts from server on mount
  useEffect(() => {
    const loadDraftsFromServer = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${API}/drafts/all`, config);
        
        const drafts = response.data.drafts || {};
        
        if (drafts.series_creation) {
          const data = drafts.series_creation;
          if (data.seriesFormData) setSeriesFormData(data.seriesFormData);
          if (data.storyBible) setStoryBible(data.storyBible);
          if (data.creationStep && data.creationStep > 1) {
            setCreationStep(data.creationStep);
            setShowNewSeries(true);
            toast.info("📂 Restored your previous progress!", { duration: 4000 });
          }
        }
        
        // NOTE: We intentionally do NOT auto-load new_book drafts
        // This was causing confusion where old outlines appeared for new books
        // Users should start fresh each time they generate a book
        // The draft is still saved for recovery purposes if needed
        if (drafts.new_book) {
          console.log("📂 Found saved book draft (not auto-loading - use 'Load Draft' if needed)");
        }
        
        console.log("📂 Loaded drafts from server");
      } catch (e) {
        console.error("Failed to load drafts:", e);
      } finally {
        setDraftsLoaded(true);
      }
    };
    
    loadDraftsFromServer();
  }, []);

  // Debounced save to server
  const saveDraftToServer = async (draftType, data) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/drafts/${draftType}`, { data }, config);
      console.log(`💾 Saved ${draftType} to server`);
    } catch (e) {
      console.error(`Failed to save ${draftType}:`, e);
    }
  };

  // Auto-save series creation data (debounced)
  useEffect(() => {
    if (!draftsLoaded) return;
    
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    saveTimeoutRef.current = setTimeout(() => {
      if (seriesFormData.name || (storyBible.core_characters?.length || 0) > 0 || creationStep > 1) {
        saveDraftToServer("series_creation", {
          seriesFormData,
          storyBible,
          creationStep: showNewSeries ? creationStep : 1
        });
      }
    }, 1000); // Save 1 second after last change
    
    return () => clearTimeout(saveTimeoutRef.current);
  }, [seriesFormData, storyBible, creationStep, showNewSeries, draftsLoaded]);

  // Auto-save new book data (debounced)
  useEffect(() => {
    if (!draftsLoaded) return;
    
    if (newBookData.title || newBookData.premise || newBookData.additional_instructions) {
      const timeoutId = setTimeout(() => {
        saveDraftToServer("new_book", newBookData);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [newBookData, draftsLoaded]);

  // Clear drafts after successful series creation
  const clearDrafts = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await axios.delete(`${API}/drafts/series_creation`, config);
        await axios.delete(`${API}/drafts/new_book`, config);
      }
      console.log("🧹 Cleared saved drafts");
    } catch (e) {
      console.error("Failed to clear drafts:", e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleRefreshContext = async (seriesId) => {
    try {
      setRefreshingContext(true);
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(
        `${API}/series/${seriesId}/refresh-context`,
        {},
        config
      );
      
      toast.success(
        `Context refreshed! Processed ${response.data.books_processed} books. ` +
        `Found: ${response.data.characters} characters, ${response.data.settings} settings, ` +
        `${response.data.plot_points} plot points.`
      );
      
      // Reload series data
      await loadData();
      
    } catch (error) {
      console.error("Refresh context error:", error);
      toast.error(error.response?.data?.detail || "Failed to refresh context");
    } finally {
      setRefreshingContext(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [seriesResponse, booksResponse] = await Promise.all([
        axios.get(`${API}/series`, config),
        axios.get(`${API}/books`, config)
      ]);
      
      setSeries(seriesResponse.data);
      // Get completed standalone books (no series_id)
      setCompletedBooks(booksResponse.data.filter(book => 
        book.status === "completed" && !book.series_id
      ));
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewSeries = () => {
    setSeriesFormData({ name: "", description: "", sourceType: "upload", targetBookNumber: 2 });
    setUploadedFiles({});
    setSelectedExistingBooks([]);
    setExtractedContext(null);
    setCreationStep(1);
    setShowNewSeries(true);
  };

  const handleSeriesCreationNext = async () => {
    if (creationStep === 1) {
      // Validate basic info
      if (!seriesFormData.name) {
        toast.error("Please enter a series name");
        return;
      }
      setCreationStep(2);
    } else if (creationStep === 2) {
      // Validate book number selection
      if (!seriesFormData.targetBookNumber || seriesFormData.targetBookNumber < 2) {
        toast.error("Please select which book in the series you're creating");
        return;
      }
      setCreationStep(3);
    } else if (creationStep === 3) {
      // Validate source selection
      const requiredBooks = seriesFormData.targetBookNumber - 1;
      if (seriesFormData.sourceType === "upload") {
        const uploadedCount = Object.keys(uploadedFiles).filter(key => uploadedFiles[key]).length;
        if (uploadedCount < requiredBooks) {
          toast.error(`Please upload all ${requiredBooks} previous book(s)`);
          return;
        }
      } else if (seriesFormData.sourceType === "existing") {
        if (selectedExistingBooks.length < requiredBooks) {
          toast.error(`Please select ${requiredBooks} existing book(s)`);
          return;
        }
      }
      
      // Extract context
      await handleExtractContext();
    } else if (creationStep === 4) {
      // Move to Story Bible setup
      setCreationStep(5);
    } else if (creationStep === 5) {
      // Create series with context AND story bible
      await handleCreateSeries();
    }
  };

  const handleFileUpload = async (bookNumber, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    const file = files[0]; // Take first file only
    
    // Validate file type
    if (!file.name.endsWith('.docx') && !file.name.endsWith('.pdf')) {
      toast.error("Only .docx and .pdf files are allowed");
      return;
    }
    
    setUploading(true);
    
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => {
        reader.onload = () => resolve({
          name: file.name,
          data: reader.result,
          size: file.size
        });
        reader.readAsDataURL(file);
      });
      
      setUploadedFiles(prev => ({
        ...prev,
        [bookNumber]: fileData
      }));
      toast.success(`Book ${bookNumber} uploaded`);
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleExtractContext = async () => {
    setExtracting(true);
    
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // First create the series
      const seriesResponse = await axios.post(`${API}/series`, {
        name: seriesFormData.name,
        description: seriesFormData.description,
        source_type: seriesFormData.sourceType,
        book_ids: seriesFormData.sourceType === "existing" ? selectedExistingBooks : [],
        target_book_number: seriesFormData.targetBookNumber
      }, config);
      
      const seriesId = seriesResponse.data.id;
      
      let booksForExtraction = [];
      
      if (seriesFormData.sourceType === "upload") {
        // Upload files to backend (in order)
        const formData = new FormData();
        const requiredBooks = seriesFormData.targetBookNumber - 1;
        
        for (let bookNum = 1; bookNum <= requiredBooks; bookNum++) {
          const fileData = uploadedFiles[bookNum];
          if (fileData) {
            // Convert data URL back to Blob
            const response = await fetch(fileData.data);
            const blob = await response.blob();
            formData.append('files', blob, fileData.name);
          }
        }
        
        const uploadResponse = await axios.post(
          `${API}/series/${seriesId}/upload-books`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        booksForExtraction = uploadResponse.data.parsed_books;
      }
      // For existing books, we'll send book_ids directly - no need to fetch anything
      
      // Extract context from books
      // For existing books, just send book_ids (lightweight request)
      // For uploaded files, send full text
      const isExistingBooks = seriesFormData.sourceType === "existing" && selectedExistingBooks.length > 0;
      
      const extractPayload = isExistingBooks 
        ? { book_ids: selectedExistingBooks }
        : { books: booksForExtraction };
      
      console.log("Sending extract payload:", isExistingBooks ? "book_ids" : "full_text", extractPayload);
      
      const extractResponse = await axios.post(
        `${API}/series/${seriesId}/extract-context`,
        extractPayload,
        { ...config, timeout: 120000 }  // 2 minute timeout
      );
      
      setExtractedContext(extractResponse.data.context);
      const combinedSeries = { ...seriesResponse.data, ...extractResponse.data.context };
      setSelectedSeries(combinedSeries);
      loadStoryBible(combinedSeries);
      setCreationStep(4);
      toast.success("Context extracted successfully!");
      
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error("Failed to extract context");
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateSeries = async () => {
    // Save the Story Bible to the series if characters were added
    if (selectedSeries?.id && ((storyBible.core_characters?.length || 0) > 0 || (storyBible.established_facts?.length || 0) > 0 || (storyBible.writing_rules?.length || 0) > 0 || (storyBible.forbidden?.length || 0) > 0)) {
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        await axios.put(
          `${API}/series/${selectedSeries.id}/story-bible`,
          storyBible,
          config
        );
        
        // Update local selectedSeries with story bible
        setSelectedSeries({ ...selectedSeries, story_bible: storyBible });
      } catch (error) {
        console.error("Failed to save Story Bible:", error);
        toast.error("Warning: Story Bible may not have saved");
      }
    }
    
    // Clear saved drafts after successful creation
    clearDrafts();
  };

  const handleUpdateContext = async (field, value) => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(
        `${API}/series/${selectedSeries.id}/context`,
        { [field]: value },
        config
      );
      
      setExtractedContext({ ...extractedContext, [field]: value });
      toast.success("Context updated");
    } catch (error) {
      toast.error("Failed to update context");
    }
  };

  // Load Story Bible when series is selected
  const loadStoryBible = (seriesData) => {
    if (seriesData?.story_bible) {
      // Ensure all required fields exist with defaults
      setStoryBible({
        core_characters: seriesData.story_bible.core_characters || [],
        established_facts: seriesData.story_bible.established_facts || [],
        writing_rules: seriesData.story_bible.writing_rules || [],
        forbidden: seriesData.story_bible.forbidden || [],
        plot_threads: seriesData.story_bible.plot_threads || []
      });
    } else {
      setStoryBible({
        core_characters: [],
        established_facts: [],
        writing_rules: [],
        forbidden: [],
        plot_threads: []
      });
    }
  };

  // Save Story Bible
  const handleSaveStoryBible = async () => {
    if (!selectedSeries) return;
    
    setSavingBible(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(
        `${API}/series/${selectedSeries.id}/story-bible`,
        storyBible,
        config
      );
      
      // Update local state
      setSelectedSeries({ ...selectedSeries, story_bible: storyBible });
      toast.success("Story Bible saved! AI will now follow these rules.");
      setShowStoryBible(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save Story Bible");
    } finally {
      setSavingBible(false);
    }
  };

  // Add character to Story Bible
  const addCharacter = () => {
    if (!newCharacter.name) {
      toast.error("Character name is required");
      return;
    }
    const currentChars = storyBible.core_characters || [];
    setStoryBible({
      ...storyBible,
      core_characters: [...currentChars, { ...newCharacter }]
    });
    setNewCharacter({ name: "", role: "", description: "", relationships: "" });
  };

  // Remove character
  const removeCharacter = (index) => {
    const currentChars = storyBible.core_characters || [];
    setStoryBible({
      ...storyBible,
      core_characters: currentChars.filter((_, i) => i !== index)
    });
  };

  const handleGenerateNextBook = async () => {
    if (!selectedSeries) {
      toast.error("No series selected");
      return;
    }
    
    if (!newBookData.title || !newBookData.premise) {
      toast.error("Please fill in title and premise");
      return;
    }
    
    setGenerating(true);
    
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(
        `${API}/series/${selectedSeries.id}/generate-book`,
        newBookData,
        config
      );
      
      const bookId = response.data.book_id;
      
      toast.success(`Book ${newBookData.series_order} generation started! Redirecting to progress...`);
      setShowGenerateBook(false);
      setNewBookData({
        title: "",
        premise: "",
        additional_instructions: "",
        series_order: 0,
        total_planned_books: 5,
        time_jump: "",
        genre: "fiction",
        total_chapters: 15,
        words_per_chapter: 3300,
        is_prequel: false,
        imported_chapter_outline: null,
        chapter_outline_text: "",
        character_roles: {},
        new_main_characters: []
      });
      
      // Clear the new book draft since it was successfully submitted
      try {
        const token = localStorage.getItem("token");
        if (token) {
          await axios.delete(`${API}/drafts/new_book`, { headers: { Authorization: `Bearer ${token}` } });
        }
      } catch (e) {
        console.error("Failed to clear new book draft:", e);
      }
      
      // Redirect to book progress page
      setTimeout(() => {
        navigate(`/book/${bookId}`);
      }, 1000);
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to start book generation");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteSeries = async (seriesId) => {
    console.log("🗑️ Delete series function called with ID:", seriesId);
    console.log("API constant value:", API);
    console.log("BACKEND_URL:", BACKEND_URL);
    
    try {
      // TEMPORARY: Skip confirmation to test if delete works
      console.log("⚠️ TESTING MODE: Skipping confirmation dialog");
      const confirmed = true; // Temporarily hardcoded
      
      // const confirmed = window.confirm("⚠️ Delete this series?\n\nBooks will be moved back to 'My Books' as standalone books.\n\nThis action cannot be undone.");
      console.log("Confirmation result (hardcoded for testing):", confirmed);
      
      if (!confirmed) {
        console.log("❌ Delete cancelled by user");
        toast.info("Delete cancelled");
        return;
      }
      
      console.log("✅ Proceeding with delete (TEST MODE)");
      
      const token = localStorage.getItem("token");
      console.log("Token exists:", !!token);
      if (!token) {
        toast.error("Not authenticated");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const deleteUrl = `${API}/series/${seriesId}`;
      
      console.log("📡 Sending DELETE request to:", deleteUrl);
      console.log("Config:", config);
      
      // Show loading toast
      toast.loading("Deleting series...", { id: "delete-series" });
      
      const response = await axios.delete(deleteUrl, config);
      console.log("✅ Delete response:", response.data);
      
      toast.success("Series deleted successfully!", { id: "delete-series" });
      
      // Reload data
      await loadData();
    } catch (error) {
      console.error("❌ Delete error FULL:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to delete: ${errorMsg}`, { id: "delete-series" });
    }
  };

  const getSeriesBookCount = (s) => {
    // Count both book_ids and uploaded_books_data
    const linkedBooks = s.book_ids?.length || 0;
    const uploadedBooks = s.uploaded_books_data?.length || 0;
    return linkedBooks + uploadedBooks;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading your series...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 ml-0 md:ml-64 py-12 px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold playfair text-stone-900 mb-2">
                Multi-Book Series
              </h1>
              <p className="text-stone-600">
                Create book series with AI-powered continuity across characters, plot, and world-building
              </p>
            </div>
            <Button 
              onClick={handleStartNewSeries} 
              size="lg"
              className="bg-amber-900 hover:bg-amber-800 text-white rounded-full shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Series
            </Button>
          </div>

          {/* Series Grid */}
          {series.length === 0 ? (
            <Card className="text-center py-16 border-stone-200 shadow-lg">
              <CardContent>
                <Library className="h-20 w-20 mx-auto text-stone-400 mb-4" />
                <h3 className="text-2xl font-semibold playfair text-stone-900 mb-2">No series yet</h3>
                <p className="text-stone-600 mb-6">
                  Create your first multi-book series with AI-powered continuity
                </p>
                <Button 
                  onClick={handleStartNewSeries}
                  className="bg-amber-900 hover:bg-amber-800 text-white rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Series
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {series.map((s) => {
                const bookCount = getSeriesBookCount(s);
                const maxBooks = s.max_books || 5;
                const canAddMore = bookCount < maxBooks;
                
                const nextBookNumber = bookCount + 1;
                
                return (
                  <Card 
                    key={s.id} 
                    className="hover:shadow-xl transition-all border-stone-200 relative"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            setSelectedSeries(s);
                            setShowSeriesDetails(true);
                          }}
                        >
                          <CardTitle className="text-2xl playfair">{s.name}</CardTitle>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            console.log("Delete button clicked for series:", s.id);
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteSeries(s.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 z-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <CardDescription className="line-clamp-2">{s.description}</CardDescription>
                    </CardHeader>
                    
                    <CardContent
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedSeries(s);
                        setShowSeriesDetails(true);
                      }}
                    >
                      <div className="space-y-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-600">Books in series:</span>
                          <Badge variant={canAddMore ? "default" : "secondary"}>
                            {bookCount} / {maxBooks}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-stone-700">{s.characters?.length || 0} Characters</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-blue-600" />
                            <span className="text-stone-700">{s.settings?.length || 0} Settings</span>
                          </div>
                        </div>
                        
                        <Button 
                          className="w-full bg-amber-900 hover:bg-amber-800 text-white z-10 relative"
                          disabled={!canAddMore}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setSelectedSeries(s);
                            setNewBookData({
                              ...newBookData,
                              series_order: nextBookNumber
                            });
                            setShowGenerateBook(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {canAddMore ? `Add Book ${nextBookNumber}` : "Series Full"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Create Series Dialog */}
          <Dialog open={showNewSeries} onOpenChange={setShowNewSeries}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  Create New Series - Step {creationStep} of 5
                </DialogTitle>
                <DialogDescription className="flex items-center justify-between">
                  <span>
                    {creationStep === 1 && "Enter basic information about your series"}
                    {creationStep === 2 && "Select which book in the series you're creating"}
                    {creationStep === 3 && "Upload previous books or select from existing"}
                    {creationStep === 4 && "Review and edit extracted story elements"}
                    {creationStep === 5 && "Set up your Story Bible - Lock in characters and rules the AI must follow"}
                  </span>
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Auto-saving
                  </span>
                </DialogDescription>
              </DialogHeader>
              
              {/* Show restore notice if we restored a draft */}
              {creationStep > 1 && (
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg text-sm">
                  <span className="text-blue-700">📂 Your progress has been auto-saved</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700 h-7"
                    onClick={() => {
                      clearDrafts();
                      setCreationStep(1);
                      setSeriesFormData({ name: "", description: "", sourceType: "upload", targetBookNumber: 2 });
                      setStoryBible({ core_characters: [], established_facts: [], writing_rules: [], forbidden: [], plot_threads: [] });
                      toast.success("Draft cleared - starting fresh");
                    }}
                  >
                    Clear & Start Over
                  </Button>
                </div>
              )}
              
              {/* Step 1: Basic Info */}
              {creationStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label>Series Name *</Label>
                    <Input
                      value={seriesFormData.name}
                      onChange={(e) => setSeriesFormData({ ...seriesFormData, name: e.target.value })}
                      placeholder="e.g., The Dragon Chronicles"
                    />
                  </div>
                  
                  <div>
                    <Label>Description (Optional)</Label>
                    <Textarea
                      value={seriesFormData.description}
                      onChange={(e) => setSeriesFormData({ ...seriesFormData, description: e.target.value })}
                      placeholder="Brief description of your series..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-900">
                      <strong>Note:</strong> Maximum 5 books per series. To extend beyond 5, create a new series using later books.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowNewSeries(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSeriesCreationNext}
                      className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                    >
                      Next: Book Number
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 2: Book Number Selection */}
              {creationStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-lg font-semibold mb-3 block">
                      Which book in the series are you creating?
                    </Label>
                    <p className="text-sm text-stone-600 mb-4">
                      Select the number of the book you want to generate. You'll need to upload all previous books.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {[2, 3, 4, 5].map((num) => (
                        <Card
                          key={num}
                          className={`cursor-pointer transition-all ${
                            seriesFormData.targetBookNumber === num
                              ? 'border-amber-600 bg-amber-50 shadow-md'
                              : 'border-stone-200 hover:border-amber-300'
                          }`}
                          onClick={() => setSeriesFormData({ ...seriesFormData, targetBookNumber: num })}
                        >
                          <CardContent className="p-6 text-center">
                            <div className="text-3xl font-bold text-amber-900 mb-2">
                              Book {num}
                            </div>
                            <div className="text-sm text-stone-600">
                              Upload {num - 1} previous book{num > 2 ? 's' : ''}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      <strong>Example:</strong> If you're creating Book 3, you'll upload Books 1 and 2 so the AI can maintain continuity.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setCreationStep(1)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleSeriesCreationNext}
                      className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                    >
                      Next: Upload Books
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 3: Upload/Select Books */}
              {creationStep === 3 && (
                <div className="space-y-4">
                  <Tabs value={seriesFormData.sourceType} onValueChange={(v) => setSeriesFormData({ ...seriesFormData, sourceType: v })}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="upload">
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Files
                      </TabsTrigger>
                      <TabsTrigger value="existing">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Use Existing Books
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="upload" className="space-y-4">
                      <p className="text-sm text-stone-600 mb-4">
                        Upload .docx or .pdf files for Books 1 through {seriesFormData.targetBookNumber - 1}
                      </p>
                      
                      <div className="space-y-3">
                        {Array.from({ length: seriesFormData.targetBookNumber - 1 }, (_, i) => i + 1).map((bookNum) => (
                          <div key={bookNum} className="border-2 border-dashed border-stone-300 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <Label className="text-base font-semibold">
                                Book {bookNum}
                              </Label>
                              {uploadedFiles[bookNum] && (
                                <Badge variant="success" className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Uploaded
                                </Badge>
                              )}
                            </div>
                            
                            {!uploadedFiles[bookNum] ? (
                              <div className="text-center py-4">
                                <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                                <Input
                                  type="file"
                                  accept=".docx,.pdf"
                                  onChange={(e) => handleFileUpload(bookNum, e)}
                                  disabled={uploading}
                                  className="max-w-xs mx-auto"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium">{uploadedFiles[bookNum].name}</span>
                                  <span className="text-xs text-stone-500">
                                    ({(uploadedFiles[bookNum].size / 1024).toFixed(0)} KB)
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newFiles = { ...uploadedFiles };
                                    delete newFiles[bookNum];
                                    setUploadedFiles(newFiles);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-amber-50 p-3 rounded border border-amber-200">
                        <p className="text-sm text-amber-900">
                          <strong>Tip:</strong> Upload books in order (Book 1 first, then Book 2, etc.) for best results.
                        </p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="existing" className="space-y-4">
                      <Label>Select {seriesFormData.targetBookNumber - 1} Completed Book(s)</Label>
                      {completedBooks.length === 0 ? (
                        <p className="text-sm text-stone-600 py-4">No completed standalone books available</p>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto border border-stone-200 rounded p-3">
                          {completedBooks.map((book) => (
                            <div 
                              key={book.id} 
                              className="flex items-center space-x-3 p-3 hover:bg-stone-50 rounded transition-colors"
                            >
                              <Checkbox
                                id={book.id}
                                checked={selectedExistingBooks.includes(book.id)}
                                disabled={!selectedExistingBooks.includes(book.id) && selectedExistingBooks.length >= (seriesFormData.targetBookNumber - 1)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedExistingBooks([...selectedExistingBooks, book.id]);
                                  } else {
                                    setSelectedExistingBooks(selectedExistingBooks.filter(id => id !== book.id));
                                  }
                                }}
                              />
                              <label htmlFor={book.id} className="flex-1 cursor-pointer">
                                <div className="font-medium text-stone-900">{book.title}</div>
                                <div className="text-sm text-stone-600 line-clamp-1">{book.description}</div>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedExistingBooks.length > 0 && (
                        <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded">
                          {selectedExistingBooks.length} book(s) selected. These will be moved to this series.
                        </p>
                      )}
                    </TabsContent>
                  </Tabs>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setCreationStep(2)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleSeriesCreationNext}
                      disabled={extracting}
                      className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                    >
                      {extracting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Extracting Context...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Extract Context with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 4: Review Context */}
              {creationStep === 4 && extractedContext && (
                <div className="space-y-6">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-1">Context Extracted!</h4>
                      <p className="text-sm text-green-800">
                        AI has analyzed your books. Review the results below. Not satisfied? Re-extract to try again.
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        setExtracting(true);
                        try {
                          const token = localStorage.getItem("token");
                          const config = { headers: { Authorization: `Bearer ${token}` } };
                          
                          // Re-extract using the series ID
                          const response = await axios.post(
                            `${API}/series/${selectedSeries.id}/re-extract`,
                            {},
                            config
                          );
                          
                          setExtractedContext(response.data.context);
                          setSelectedSeries({ ...selectedSeries, ...response.data.context });
                          toast.success("Context re-extracted successfully!");
                        } catch (error) {
                          console.error("Re-extract error:", error);
                          toast.error("Failed to re-extract context");
                        } finally {
                          setExtracting(false);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      disabled={extracting}
                      className="whitespace-nowrap"
                    >
                      {extracting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Re-extracting...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Re-extract
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Warning if insufficient extraction */}
                  {(extractedContext.characters?.length === 0 || extractedContext.settings?.length === 0) && (
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-amber-900 mb-1">Low Extraction Results</h4>
                        <p className="text-sm text-amber-800">
                          Few or no {extractedContext.characters?.length === 0 ? 'characters' : 'settings'} were extracted. 
                          Consider clicking "Re-extract" to try again, or proceed if this is expected.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">{extractedContext.characters?.length || 0}</div>
                      <div className="text-sm text-blue-900">Characters</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600">{extractedContext.settings?.length || 0}</div>
                      <div className="text-sm text-purple-900">Settings</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="text-3xl font-bold text-amber-600">{extractedContext.plot_points?.length || 0}</div>
                      <div className="text-sm text-amber-900">Plot Points</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-3xl font-bold text-green-600">
                        {extractedContext.tone_analysis ? "✓" : "—"}
                      </div>
                      <div className="text-sm text-green-900">Tone Analyzed</div>
                    </div>
                  </div>
                  
                  <Tabs defaultValue="characters" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="characters">Characters</TabsTrigger>
                      <TabsTrigger value="settings">Settings</TabsTrigger>
                      <TabsTrigger value="plot">Plot</TabsTrigger>
                      <TabsTrigger value="tone">Tone & Style</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="characters" className="space-y-3 max-h-96 overflow-y-auto">
                      {extractedContext.characters?.map((char, idx) => (
                        <Card key={idx} className="p-4">
                          <h4 className="font-semibold text-lg mb-2">{char.name}</h4>
                          <p className="text-sm text-stone-600 mb-2">{char.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {char.traits?.map((trait, i) => (
                              <Badge key={i} variant="outline">{trait}</Badge>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </TabsContent>
                    
                    <TabsContent value="settings" className="space-y-3 max-h-96 overflow-y-auto">
                      {extractedContext.settings?.map((setting, idx) => (
                        <Card key={idx} className="p-4">
                          <h4 className="font-semibold text-lg mb-2">{setting.name}</h4>
                          <p className="text-sm text-stone-600">{setting.description}</p>
                        </Card>
                      ))}
                    </TabsContent>
                    
                    <TabsContent value="plot" className="space-y-2 max-h-96 overflow-y-auto">
                      {extractedContext.plot_points?.map((point, idx) => (
                        <div key={idx} className="p-3 bg-stone-50 rounded border border-stone-200">
                          <div className="flex gap-2">
                            <span className="font-semibold text-amber-600">#{idx + 1}</span>
                            <p className="text-sm text-stone-700">{point}</p>
                          </div>
                        </div>
                      ))}
                    </TabsContent>
                    
                    <TabsContent value="tone" className="space-y-4">
                      {extractedContext.tone_analysis && (
                        <div className="space-y-3">
                          <div>
                            <Label className="font-semibold">Tone</Label>
                            <p className="text-sm text-stone-700 mt-1">{extractedContext.tone_analysis.tone}</p>
                          </div>
                          <div>
                            <Label className="font-semibold">Voice</Label>
                            <p className="text-sm text-stone-700 mt-1">{extractedContext.tone_analysis.voice}</p>
                          </div>
                          <div>
                            <Label className="font-semibold">Pacing</Label>
                            <p className="text-sm text-stone-700 mt-1">{extractedContext.tone_analysis.pacing}</p>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline"
                      onClick={() => setCreationStep(3)}
                      className="flex-1"
                    >
                      Back to Upload
                    </Button>
                    <Button 
                      onClick={() => setCreationStep(5)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Next: Set Up Story Bible
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Story Bible Setup */}
              {creationStep === 5 && (
                <div className="space-y-6">
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Story Bible - Lock In Your Series Rules
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      Define the characters and rules the AI MUST follow. This ensures consistency across all books in your series.
                    </p>
                  </div>

                  {/* Core Characters Section */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      👥 Core Characters
                      <span className="text-sm font-normal text-gray-500">(AI will ONLY use these as main characters)</span>
                    </h3>
                    
                    {/* Select from Extracted Characters */}
                    {extractedContext?.characters?.length > 0 && (
                      <div className="p-3 border border-purple-200 rounded-lg bg-purple-50/50">
                        <p className="text-sm font-medium text-purple-800 mb-2">Select from extracted characters:</p>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {extractedContext.characters.map((char, idx) => {
                            const coreChars = storyBible.core_characters || [];
                            const isSelected = coreChars.some(c => c.name === char.name);
                            return (
                              <div 
                                key={idx} 
                                className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                                  isSelected 
                                    ? 'bg-purple-100 border-purple-400' 
                                    : 'bg-white border-gray-200 hover:border-purple-300'
                                }`}
                                onClick={() => {
                                  const currentChars = storyBible.core_characters || [];
                                  if (isSelected) {
                                    // Remove from story bible
                                    setStoryBible({
                                      ...storyBible,
                                      core_characters: currentChars.filter(c => c.name !== char.name)
                                    });
                                  } else {
                                    // Add to story bible
                                    const roleText = char.role || ((typeof char.description === 'string' && char.description) ? char.description.split('.')[0] : '') || '';
                                    setStoryBible({
                                      ...storyBible,
                                      core_characters: [...currentChars, {
                                        name: char.name || '',
                                        role: roleText,
                                        description: (typeof char.description === 'string') ? char.description : '',
                                        relationships: (typeof char.relationships === 'string') ? char.relationships : ''
                                      }]
                                    });
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                    isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                                  }`}>
                                    {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{char.name}</div>
                                    {char.role && <div className="text-xs text-gray-500 truncate">{char.role}</div>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Selected Characters */}
                    {(storyBible.core_characters?.length || 0) > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        <p className="text-sm font-medium text-gray-700">Selected characters ({storyBible.core_characters?.length || 0}):</p>
                        {(storyBible.core_characters || []).map((char, index) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                            <div className="flex-1">
                              <div className="font-medium">{char.name}</div>
                              <div className="text-sm text-gray-600">{char.role}</div>
                              {char.description && <div className="text-xs text-gray-500">{char.description}</div>}
                              {char.relationships && <div className="text-xs text-purple-600">Relationships: {char.relationships}</div>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeCharacter(index)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {(storyBible.core_characters?.length || 0) === 0 && !extractedContext?.characters?.length && (
                      <p className="text-sm text-gray-500 italic p-2">No characters extracted. Add your main characters manually below.</p>
                    )}
                    
                    {/* Add Custom Character */}
                    <details className="border rounded-lg">
                      <summary className="p-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                        + Add custom character manually
                      </summary>
                      <div className="grid grid-cols-2 gap-2 p-3 border-t bg-gray-50">
                        <Input
                          placeholder="Character Name *"
                          value={newCharacter.name}
                          onChange={(e) => setNewCharacter({ ...newCharacter, name: e.target.value })}
                        />
                        <Input
                          placeholder="Role (e.g., The President)"
                          value={newCharacter.role}
                          onChange={(e) => setNewCharacter({ ...newCharacter, role: e.target.value })}
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={newCharacter.description}
                          onChange={(e) => setNewCharacter({ ...newCharacter, description: e.target.value })}
                        />
                        <Input
                          placeholder="Relationships (e.g., married to Elaina)"
                          value={newCharacter.relationships}
                          onChange={(e) => setNewCharacter({ ...newCharacter, relationships: e.target.value })}
                        />
                        <Button onClick={addCharacter} className="col-span-2" variant="outline">
                          <Plus className="h-4 w-4 mr-2" /> Add Character
                        </Button>
                      </div>
                    </details>
                  </div>

                  {/* Established Facts Section */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">📜 Established Facts <span className="text-sm font-normal text-gray-500">(Cannot be contradicted)</span></h3>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {(storyBible.established_facts || []).map((fact, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                          <span className="flex-1 text-sm">{fact}</span>
                          <Button variant="ghost" size="sm" onClick={() => setStoryBible({
                            ...storyBible,
                            established_facts: (storyBible.established_facts || []).filter((_, i) => i !== index)
                          })}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Elaina was detained in an immigrant camp"
                        value={newFact}
                        onChange={(e) => setNewFact(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newFact.trim()) {
                            setStoryBible({ ...storyBible, established_facts: [...(storyBible.established_facts || []), newFact.trim()] });
                            setNewFact("");
                          }
                        }}
                      />
                      <Button variant="outline" onClick={() => {
                        if (newFact.trim()) {
                          setStoryBible({ ...storyBible, established_facts: [...(storyBible.established_facts || []), newFact.trim()] });
                          setNewFact("");
                        }
                      }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Writing Rules & Forbidden - Collapsed into two columns */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Writing Rules */}
                    <div className="space-y-2">
                      <h3 className="font-semibold">📝 Writing Rules</h3>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {(storyBible.writing_rules || []).map((rule, index) => (
                          <div key={index} className="flex items-center gap-1 p-1 bg-green-50 rounded text-xs">
                            <span className="flex-1">{rule}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setStoryBible({
                              ...storyBible,
                              writing_rules: (storyBible.writing_rules || []).filter((_, i) => i !== index)
                            })}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Input
                          placeholder="e.g., Political thriller tone"
                          className="text-sm"
                          value={newRule}
                          onChange={(e) => setNewRule(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newRule.trim()) {
                              setStoryBible({ ...storyBible, writing_rules: [...(storyBible.writing_rules || []), newRule.trim()] });
                              setNewRule("");
                            }
                          }}
                        />
                        <Button variant="outline" size="sm" onClick={() => {
                          if (newRule.trim()) {
                            setStoryBible({ ...storyBible, writing_rules: [...(storyBible.writing_rules || []), newRule.trim()] });
                            setNewRule("");
                          }
                        }}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Forbidden */}
                    <div className="space-y-2">
                      <h3 className="font-semibold">🚫 Forbidden</h3>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {(storyBible.forbidden || []).map((item, index) => (
                          <div key={index} className="flex items-center gap-1 p-1 bg-red-50 rounded text-xs">
                            <span className="flex-1 text-red-700">❌ {item}</span>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setStoryBible({
                              ...storyBible,
                              forbidden: (storyBible.forbidden || []).filter((_, i) => i !== index)
                            })}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Input
                          placeholder="e.g., No new main characters"
                          className="text-sm"
                          value={newForbidden}
                          onChange={(e) => setNewForbidden(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newForbidden.trim()) {
                              setStoryBible({ ...storyBible, forbidden: [...(storyBible.forbidden || []), newForbidden.trim()] });
                              setNewForbidden("");
                            }
                          }}
                        />
                        <Button variant="outline" size="sm" onClick={() => {
                          if (newForbidden.trim()) {
                            setStoryBible({ ...storyBible, forbidden: [...(storyBible.forbidden || []), newForbidden.trim()] });
                            setNewForbidden("");
                          }
                        }}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button 
                      variant="outline"
                      onClick={() => setCreationStep(4)}
                      className="flex-1"
                    >
                      Back to Context
                    </Button>
                    <Button 
                      onClick={async () => {
                        // Create series with story bible
                        await handleCreateSeries();
                        
                        // Close and reset
                        setShowNewSeries(false);
                        setCreationStep(1);
                        await loadData();
                        
                        toast.success("Series created with Story Bible!");
                        
                        setTimeout(() => {
                          setNewBookData({
                            ...newBookData,
                            series_order: selectedSeries?.target_book_number || 2
                          });
                          setShowGenerateBook(true);
                        }, 500);
                      }}
                      className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Create Series & Generate Book
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Series Details Dialog */}
          <Dialog open={showSeriesDetails} onOpenChange={setShowSeriesDetails}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {selectedSeries?.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedSeries?.description || "View and manage your series"}
                </DialogDescription>
              </DialogHeader>
              
              {selectedSeries && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Story Context</h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          loadStoryBible(selectedSeries);
                          setShowStoryBible(true);
                        }}
                        variant="default"
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Story Bible {selectedSeries?.story_bible?.core_characters?.length > 0 && `(${selectedSeries.story_bible.core_characters.length})`}
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            toast.loading("Re-extracting context...", { id: "re-extract" });
                            const token = localStorage.getItem("token");
                            const config = { headers: { Authorization: `Bearer ${token}` } };
                            
                            const response = await axios.post(
                              `${API}/series/${selectedSeries.id}/re-extract`,
                              {},
                              config
                            );
                            
                            toast.success("Context re-extracted successfully!", { id: "re-extract" });
                            
                            // Update the selected series with new context
                            setSelectedSeries({
                              ...selectedSeries,
                              ...response.data.context
                            });
                            
                            // Reload data
                            await loadData();
                          } catch (error) {
                            console.error("Re-extract error:", error);
                            toast.error("Failed to re-extract context", { id: "re-extract" });
                          }
                        }}
                        variant="outline"
                        size="sm"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Re-extract Context
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">
                        {selectedSeries.characters?.length || 0}
                      </div>
                      <div className="text-sm text-blue-900">Characters</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600">
                        {selectedSeries.settings?.length || 0}
                      </div>
                      <div className="text-sm text-purple-900">Settings</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="text-3xl font-bold text-amber-600">
                        {selectedSeries.plot_points?.length || 0}
                      </div>
                      <div className="text-sm text-amber-900">Plot Points</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-3xl font-bold text-green-600">
                        {getSeriesBookCount(selectedSeries)}
                      </div>
                      <div className="text-sm text-green-900">Books in Series</div>
                    </div>
                  </div>
                  
                  {(selectedSeries.characters?.length === 0 && 
                    selectedSeries.settings?.length === 0 && 
                    selectedSeries.plot_points?.length === 0) && (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-red-900 mb-1">No Context Extracted</h4>
                        <p className="text-sm text-red-800">
                          Context extraction may have failed. Click "Re-extract Context" to try again.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <Tabs defaultValue="characters" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="characters">Characters</TabsTrigger>
                      <TabsTrigger value="settings">Settings</TabsTrigger>
                      <TabsTrigger value="plot">Plot</TabsTrigger>
                      <TabsTrigger value="tone">Tone & Style</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="characters" className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedSeries.characters?.length > 0 ? (
                        selectedSeries.characters.map((char, idx) => (
                          <Card key={idx} className="p-4">
                            <h4 className="font-semibold text-lg mb-2">{char.name}</h4>
                            <p className="text-sm text-stone-600 mb-2">{char.description}</p>
                            {char.traits && (
                              <div className="flex flex-wrap gap-1">
                                {char.traits.map((trait, i) => (
                                  <Badge key={i} variant="outline">{trait}</Badge>
                                ))}
                              </div>
                            )}
                          </Card>
                        ))
                      ) : (
                        <p className="text-center text-stone-500 py-8">No characters extracted</p>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="settings" className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedSeries.settings?.length > 0 ? (
                        selectedSeries.settings.map((setting, idx) => (
                          <Card key={idx} className="p-4">
                            <h4 className="font-semibold text-lg mb-2">{setting.name}</h4>
                            <p className="text-sm text-stone-600">{setting.description}</p>
                          </Card>
                        ))
                      ) : (
                        <p className="text-center text-stone-500 py-8">No settings extracted</p>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="plot" className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedSeries.plot_points?.length > 0 ? (
                        selectedSeries.plot_points.map((point, idx) => (
                          <div key={idx} className="p-3 bg-stone-50 rounded border border-stone-200">
                            <div className="flex gap-2">
                              <span className="font-semibold text-amber-600">#{idx + 1}</span>
                              <p className="text-sm text-stone-700">{point}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-stone-500 py-8">No plot points extracted</p>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="tone" className="space-y-4">
                      {selectedSeries.tone_analysis ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="font-semibold">Tone</Label>
                            <p className="text-sm text-stone-700 mt-1">{selectedSeries.tone_analysis.tone}</p>
                          </div>
                          <div>
                            <Label className="font-semibold">Voice</Label>
                            <p className="text-sm text-stone-700 mt-1">{selectedSeries.tone_analysis.voice}</p>
                          </div>
                          <div>
                            <Label className="font-semibold">Pacing</Label>
                            <p className="text-sm text-stone-700 mt-1">{selectedSeries.tone_analysis.pacing}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-center text-stone-500 py-8">No tone analysis available</p>
                      )}
                    </TabsContent>
                  </Tabs>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between items-center gap-3 pt-4 border-t">
                    <Button
                      onClick={() => handleRefreshContext(selectedSeries.id)}
                      disabled={refreshingContext}
                      variant="outline"
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      {refreshingContext ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh Context from All Books
                        </>
                      )}
                    </Button>
                    
                    {(() => {
                      const bookCount = getSeriesBookCount(selectedSeries);
                      const maxBooks = selectedSeries.max_books || 5;
                      const canAddMore = bookCount < maxBooks;
                      const nextBookNumber = bookCount + 1;
                      
                      return (
                        <Button
                          onClick={() => {
                            setShowSeriesDetails(false);
                            setNewBookData({
                              ...newBookData,
                              series_order: nextBookNumber
                            });
                            setShowGenerateBook(true);
                          }}
                          disabled={!canAddMore}
                          className="bg-amber-900 hover:bg-amber-800 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {canAddMore ? `Add Book ${nextBookNumber}` : "Series Full (5/5)"}
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Generate New Book Dialog */}
          <Dialog open={showGenerateBook} onOpenChange={(open) => {
            setShowGenerateBook(open);
            // Reset form when closing the dialog
            if (!open) {
              setNewBookData({
                title: "",
                premise: "",
                additional_instructions: "",
                series_order: selectedSeries ? (selectedSeries.book_ids?.length || 0) + 1 : 1,
                total_planned_books: 5,
                time_jump: "",
                genre: "fiction",
                total_chapters: 15,
                words_per_chapter: 3300,
                is_prequel: false,
                imported_chapter_outline: null,
                chapter_outline_text: "",
                character_roles: {},
                new_main_characters: []
              });
            }
          }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              {selectedSeries ? (
                <>
                  <DialogHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <DialogTitle className="text-2xl">
                          Generate Book {newBookData.series_order} for "{selectedSeries.name}"
                        </DialogTitle>
                        <DialogDescription>
                          Create the next book in your series with AI-powered continuity
                        </DialogDescription>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => {
                          setNewBookData({
                            title: "",
                            premise: "",
                            additional_instructions: "",
                            series_order: selectedSeries ? (selectedSeries.book_ids?.length || 0) + 1 : 1,
                            total_planned_books: 5,
                            time_jump: "",
                            genre: "fiction",
                            total_chapters: 15,
                            words_per_chapter: 3300,
                            is_prequel: false,
                            imported_chapter_outline: null,
                            chapter_outline_text: "",
                            character_roles: {},
                            new_main_characters: []
                          });
                          toast.info("Form cleared");
                        }}
                      >
                        🗑️ Clear Form
                      </Button>
                    </div>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Book Title *</Label>
                    <Input
                      value={newBookData.title}
                      onChange={(e) => setNewBookData({ ...newBookData, title: e.target.value })}
                      placeholder="e.g., The Final Battle"
                    />
                  </div>
                  
                  <div>
                    <Label>Book Number in Series</Label>
                    <Input
                      type="number"
                      value={newBookData.series_order}
                      onChange={(e) => setNewBookData({ ...newBookData, series_order: parseInt(e.target.value) })}
                      min={1}
                      max={10}
                    />
                  </div>
                </div>
                
                {/* Series Scope - helps AI understand book's role */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Books Planned</Label>
                    <Input
                      type="number"
                      value={newBookData.total_planned_books || 5}
                      onChange={(e) => setNewBookData({ ...newBookData, total_planned_books: parseInt(e.target.value) })}
                      min={newBookData.series_order || 2}
                      max={20}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Helps AI understand this book's role (e.g., penultimate book = false victory)
                    </p>
                  </div>
                  <div className="flex items-end">
                    {newBookData.series_order && newBookData.total_planned_books && (
                      <p className="text-sm p-2 bg-blue-50 rounded border border-blue-200 text-blue-800">
                        {newBookData.series_order === newBookData.total_planned_books 
                          ? "📖 Final Book: Full resolution expected"
                          : newBookData.series_order === (newBookData.total_planned_books - 1)
                          ? "⚔️ Penultimate: Clear victory, then hints of more"
                          : newBookData.series_order === 1
                          ? "🌅 Foundation: Establish threat & cast"
                          : "📈 Middle Book: Escalate & complicate"}
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label>Premise/Synopsis *</Label>
                  <Textarea
                    value={newBookData.premise}
                    onChange={(e) => setNewBookData({ ...newBookData, premise: e.target.value })}
                    placeholder="What happens in this book? Describe the main plot, conflicts, and character developments..."
                    rows={4}
                  />
                </div>
                
                <div>
                  <Label>Additional Instructions (Optional)</Label>
                  <Textarea
                    value={newBookData.additional_instructions}
                    onChange={(e) => setNewBookData({ ...newBookData, additional_instructions: e.target.value })}
                    placeholder="Any specific directions for the AI? (e.g., writing style preferences, themes to emphasize, plot twists to include, etc.)"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>Time Jump (Optional)</Label>
                  <Input
                    value={newBookData.time_jump}
                    onChange={(e) => setNewBookData({ ...newBookData, time_jump: e.target.value })}
                    placeholder="e.g., 'Takes place 5 years later' or 'Immediately after Book 1'"
                  />
                </div>
                
                {/* Prequel Toggle */}
                <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <input
                    type="checkbox"
                    id="is_prequel"
                    checked={newBookData.is_prequel}
                    onChange={(e) => setNewBookData({ ...newBookData, is_prequel: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_prequel" className="text-amber-800 cursor-pointer">
                    📜 This is a <strong>PREQUEL</strong> (different protagonist/time period than main series)
                  </Label>
                </div>
                
                {/* Character Role Selector - NEW */}
                {selectedSeries?.characters && selectedSeries.characters.length > 0 && (
                  <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                    <Label className="text-base font-semibold flex items-center gap-2 text-blue-900">
                      👥 Character Roles for This Book
                    </Label>
                    <p className="text-sm text-blue-700 mb-3">
                      Set who are the MAIN vs SUPPORTING characters in this specific book. This helps the AI focus on the right characters.
                    </p>
                    
                    <div className="space-y-2 mb-4">
                      {selectedSeries.characters.slice(0, 10).map((char, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex-1">
                            <span className="font-medium text-stone-800">{char.name}</span>
                            {char.role && <span className="text-sm text-stone-500 ml-2">({char.role})</span>}
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setNewBookData({
                                ...newBookData,
                                character_roles: { ...newBookData.character_roles, [char.name]: "main" }
                              })}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                newBookData.character_roles[char.name] === "main"
                                  ? "bg-green-600 text-white"
                                  : "bg-stone-100 text-stone-600 hover:bg-green-100"
                              }`}
                            >
                              Main
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewBookData({
                                ...newBookData,
                                character_roles: { ...newBookData.character_roles, [char.name]: "supporting" }
                              })}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                newBookData.character_roles[char.name] === "supporting"
                                  ? "bg-blue-600 text-white"
                                  : "bg-stone-100 text-stone-600 hover:bg-blue-100"
                              }`}
                            >
                              Supporting
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewBookData({
                                ...newBookData,
                                character_roles: { ...newBookData.character_roles, [char.name]: "absent" }
                              })}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                newBookData.character_roles[char.name] === "absent"
                                  ? "bg-stone-600 text-white"
                                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                              }`}
                            >
                              Not in Book
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Main Character */}
                    <div className="border-t border-blue-200 pt-3">
                      <p className="text-sm font-medium text-blue-800 mb-2">➕ Add New Main Character for This Book</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Character name (e.g., Marcus)"
                          value={newBookData.new_main_characters.length > 0 && newBookData.new_main_characters[newBookData.new_main_characters.length - 1]?.name === "" 
                            ? "" 
                            : ""}
                          onChange={(e) => {
                            // Handled by the Add button
                          }}
                          id="new-char-name"
                          className="flex-1 bg-white"
                        />
                        <Input
                          placeholder="Role (e.g., Leo's mentor)"
                          id="new-char-role"
                          className="flex-1 bg-white"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-blue-600 text-blue-700"
                          onClick={() => {
                            const nameInput = document.getElementById('new-char-name');
                            const roleInput = document.getElementById('new-char-role');
                            const name = nameInput?.value?.trim();
                            const role = roleInput?.value?.trim();
                            if (name) {
                              setNewBookData({
                                ...newBookData,
                                new_main_characters: [...newBookData.new_main_characters, { name, role: role || "main character" }],
                                character_roles: { ...newBookData.character_roles, [name]: "main" }
                              });
                              if (nameInput) nameInput.value = "";
                              if (roleInput) roleInput.value = "";
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                      
                      {/* Show added new characters */}
                      {newBookData.new_main_characters.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newBookData.new_main_characters.map((char, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                              <span className="font-medium">{char.name}</span>
                              <span className="text-green-600">({char.role})</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = newBookData.new_main_characters.filter((_, i) => i !== idx);
                                  const updatedRoles = { ...newBookData.character_roles };
                                  delete updatedRoles[char.name];
                                  setNewBookData({
                                    ...newBookData,
                                    new_main_characters: updated,
                                    character_roles: updatedRoles
                                  });
                                }}
                                className="ml-1 text-green-600 hover:text-red-600"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Summary of selections */}
                    {Object.keys(newBookData.character_roles).length > 0 && (
                      <div className="mt-3 p-2 bg-white rounded border border-blue-200">
                        <p className="text-xs font-medium text-blue-800 mb-1">Selected Roles:</p>
                        <div className="text-xs text-stone-600">
                          <span className="text-green-700 font-medium">Main: </span>
                          {Object.entries(newBookData.character_roles)
                            .filter(([_, role]) => role === "main")
                            .map(([name]) => name)
                            .join(", ") || "None selected"}
                          <span className="mx-2">|</span>
                          <span className="text-blue-700 font-medium">Supporting: </span>
                          {Object.entries(newBookData.character_roles)
                            .filter(([_, role]) => role === "supporting")
                            .map(([name]) => name)
                            .join(", ") || "None selected"}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Chapter Outline Input */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    📋 Custom Chapter Outline (Optional but Recommended)
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Provide your own chapter-by-chapter outline to ensure the AI follows YOUR story exactly.
                    Format: One chapter per line with "CHAPTER X — Title" followed by description lines.
                  </p>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-stone-500">
                      {newBookData.imported_chapter_outline?.length > 0 
                        ? `✅ ${newBookData.imported_chapter_outline.length} chapters detected`
                        : "Paste your outline below"}
                    </span>
                    {newBookData.chapter_outline_text && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-red-600 hover:text-red-800"
                        onClick={() => setNewBookData({
                          ...newBookData,
                          chapter_outline_text: "",
                          imported_chapter_outline: null
                        })}
                      >
                        Clear Outline
                      </Button>
                    )}
                  </div>
                  <Textarea
                    value={newBookData.chapter_outline_text || ""}
                    onChange={(e) => {
                      const text = e.target.value;
                      // Enhanced parser for multi-line outlines
                      // Handles formats like:
                      // - "CHAPTER 1 — Title" with description on next line
                      // - "ACT I" headers (skipped)
                      // - "Chapter 1: Title - Description" on single line
                      
                      const lines = text.split('\n');
                      const chapters = [];
                      let currentChapter = null;
                      let chapterCount = 0;
                      
                      for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (!line) continue;
                        
                        // Skip ACT headers, Theme lines, section headers
                        if (/^ACT\s+[IVX]+/i.test(line) || /^Theme:/i.test(line) || /^Opening Action/i.test(line) || /^Key Revelations/i.test(line) || /^Flashback/i.test(line) || /^Cliffhanger/i.test(line) || /^Twist/i.test(line) || /^Action/i.test(line) || /^Revelation/i.test(line) || /^Moral/i.test(line) || /^New Player/i.test(line) || /^New POV/i.test(line) || /^Reveal/i.test(line) || /^Immediate/i.test(line) || /^Emotional/i.test(line) || /^Climax/i.test(line) || /^Resolution/i.test(line) || /^Final Line/i.test(line) || /^Risk/i.test(line) || /^Red Flags/i.test(line) || /^Psychological/i.test(line) || /^World Reaction/i.test(line) || /^Philosophy/i.test(line) || /^Major Reveal/i.test(line) || /^Jake's Role/i.test(line)) {
                          continue;
                        }
                        
                        // Check for chapter line: "CHAPTER X — Title" or "Chapter X: Title"
                        const chapterMatch = line.match(/^CHAPTER\s*(\d+)\s*[—\-:]+\s*(.+)$/i);
                        
                        if (chapterMatch) {
                          // Save previous chapter if exists
                          if (currentChapter) {
                            chapters.push(currentChapter);
                          }
                          
                          chapterCount++;
                          const chapterNum = parseInt(chapterMatch[1]) || chapterCount;
                          const title = chapterMatch[2].trim();
                          
                          currentChapter = {
                            chapter_number: chapterNum,
                            title: title,
                            content: "",
                            summary: ""
                          };
                        } else if (currentChapter) {
                          // This is a description line for the current chapter
                          // Skip bullet points that are just headers
                          if (line.startsWith('•') || line.startsWith('-')) {
                            continue;
                          }
                          if (currentChapter.content) {
                            currentChapter.content += " " + line;
                            currentChapter.summary += " " + line;
                          } else {
                            currentChapter.content = line;
                            currentChapter.summary = line;
                          }
                        }
                      }
                      
                      // Don't forget the last chapter
                      if (currentChapter) {
                        chapters.push(currentChapter);
                      }
                      
                      console.log(`📋 Parsed ${chapters.length} chapters from outline`);
                      if (chapters.length > 0) {
                        console.log("First 3 chapters:", chapters.slice(0, 3));
                      }
                      
                      setNewBookData({ 
                        ...newBookData, 
                        chapter_outline_text: text,
                        imported_chapter_outline: chapters.length > 0 ? chapters : null,
                        total_chapters: chapters.length > 0 ? chapters.length : newBookData.total_chapters
                      });
                    }}
                    placeholder={`CHAPTER 1 — No Safe Ground
Jake survives an assassination attempt within the first 5 pages.
Attack happens in what should be a "safe" European city.

CHAPTER 2 — The Kill Authority
Jake realizes a kill authorization protocol has been activated.
This is above agency level — requires multi-national consent.

CHAPTER 3 — Asset or Liability
Jake is hunted by government teams, private contractors, and former allies.`}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  {newBookData.imported_chapter_outline && newBookData.imported_chapter_outline.length > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      ✅ {newBookData.imported_chapter_outline.length} chapters detected. The AI will follow this outline.
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Genre</Label>
                    <Select value={newBookData.genre} onValueChange={(v) => setNewBookData({ ...newBookData, genre: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fiction">Fiction</SelectItem>
                        <SelectItem value="non-fiction">Non-Fiction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Total Chapters</Label>
                    <Input
                      type="number"
                      value={newBookData.total_chapters}
                      onChange={(e) => setNewBookData({ ...newBookData, total_chapters: parseInt(e.target.value) })}
                      min={5}
                      max={50}
                    />
                  </div>
                  
                  <div>
                    <Label>Words Per Chapter</Label>
                    <Select 
                      value={String(newBookData.words_per_chapter)} 
                      onValueChange={(v) => setNewBookData({ ...newBookData, words_per_chapter: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2500">2,500 words</SelectItem>
                        <SelectItem value="3000">3,000 words</SelectItem>
                        <SelectItem value="3300">3,300 words (recommended)</SelectItem>
                        <SelectItem value="3500">3,500 words</SelectItem>
                        <SelectItem value="4000">4,000 words</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Total: ~{(newBookData.total_chapters * newBookData.words_per_chapter).toLocaleString()} words
                    </p>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">AI Will Maintain:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• {selectedSeries?.characters?.length || 0} characters from previous books</li>
                    <li>• {selectedSeries?.settings?.length || 0} established settings</li>
                    <li>• {selectedSeries?.plot_points?.length || 0} plot points for continuity</li>
                    <li>• Original tone and writing style</li>
                  </ul>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowGenerateBook(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleGenerateNextBook}
                    disabled={generating}
                    className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Starting Generation...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Book
                      </>
                                       )}
                  </Button>
                </div>
              </div>
                </>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-stone-500">Loading series information...</p>
                </div>
              )}
            </DialogContent>
          </Dialog>
          {/* Story Bible Dialog */}
          <Dialog open={showStoryBible} onOpenChange={setShowStoryBible}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                  Story Bible - {selectedSeries?.name}
                </DialogTitle>
                <DialogDescription>
                  Define the locked-in facts that the AI MUST follow. Characters listed here will be the ONLY protagonists used.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Core Characters Section */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    👥 Core Characters
                    <span className="text-sm font-normal text-gray-500">(AI will ONLY use these characters)</span>
                  </h3>
                  
                  {/* Select from Extracted Characters (for existing series) */}
                  {selectedSeries?.characters?.length > 0 && (
                    <div className="p-3 border border-purple-200 rounded-lg bg-purple-50/50">
                      <p className="text-sm font-medium text-purple-800 mb-2">Select from extracted characters:</p>
                      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                        {selectedSeries.characters.map((char, idx) => {
                          const coreChars = storyBible.core_characters || [];
                          const isSelected = coreChars.some(c => c.name === char.name);
                          return (
                            <div 
                              key={idx} 
                              className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                                isSelected 
                                  ? 'bg-purple-100 border-purple-400' 
                                  : 'bg-white border-gray-200 hover:border-purple-300'
                              }`}
                              onClick={() => {
                                const currentChars = storyBible.core_characters || [];
                                if (isSelected) {
                                  // Remove from story bible
                                  setStoryBible({
                                    ...storyBible,
                                    core_characters: currentChars.filter(c => c.name !== char.name)
                                  });
                                } else {
                                  // Add to story bible
                                  const roleText = char.role || ((typeof char.description === 'string' && char.description) ? char.description.split('.')[0] : '') || '';
                                  setStoryBible({
                                    ...storyBible,
                                    core_characters: [...currentChars, {
                                      name: char.name || '',
                                      role: roleText,
                                      description: (typeof char.description === 'string') ? char.description : '',
                                      relationships: (typeof char.relationships === 'string') ? char.relationships : ''
                                    }]
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                  isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                                }`}>
                                  {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{char.name}</div>
                                  {char.role && <div className="text-xs text-gray-500 truncate">{char.role}</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Selected/Existing Characters */}
                  {/* Selected Characters */}
                  {(storyBible.core_characters?.length || 0) > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-gray-700">Selected characters ({storyBible.core_characters?.length || 0}):</p>
                      {(storyBible.core_characters || []).map((char, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium">{char.name}</div>
                            <div className="text-sm text-gray-600">{char.role}</div>
                            {char.description && <div className="text-xs text-gray-500">{char.description}</div>}
                            {char.relationships && <div className="text-xs text-purple-600">Relationships: {char.relationships}</div>}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeCharacter(index)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(storyBible.core_characters?.length || 0) === 0 && !selectedSeries?.characters?.length && (
                    <p className="text-sm text-gray-500 italic p-2">No characters extracted. Add your main characters manually below.</p>
                  )}
                  
                  {/* Add Custom Character */}
                  <details className="border rounded-lg">
                    <summary className="p-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                      + Add custom character manually
                    </summary>
                    <div className="grid grid-cols-2 gap-2 p-3 border-t bg-gray-50">
                      <Input
                        placeholder="Character Name *"
                        value={newCharacter.name}
                        onChange={(e) => setNewCharacter({ ...newCharacter, name: e.target.value })}
                      />
                      <Input
                        placeholder="Role (e.g., The President)"
                        value={newCharacter.role}
                        onChange={(e) => setNewCharacter({ ...newCharacter, role: e.target.value })}
                      />
                      <Input
                        placeholder="Description (optional)"
                        value={newCharacter.description}
                        onChange={(e) => setNewCharacter({ ...newCharacter, description: e.target.value })}
                      />
                      <Input
                        placeholder="Relationships (e.g., married to Elaina)"
                        value={newCharacter.relationships}
                        onChange={(e) => setNewCharacter({ ...newCharacter, relationships: e.target.value })}
                      />
                      <Button onClick={addCharacter} className="col-span-2" variant="outline">
                        <Plus className="h-4 w-4 mr-2" /> Add Character
                      </Button>
                    </div>
                  </details>
                </div>

                {/* Established Facts Section */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">📜 Established Facts <span className="text-sm font-normal text-gray-500">(Things that happened and cannot be contradicted)</span></h3>
                  <div className="space-y-2">
                    {(storyBible.established_facts || []).map((fact, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                        <span className="flex-1 text-sm">{fact}</span>
                        <Button variant="ghost" size="sm" onClick={() => setStoryBible({
                          ...storyBible,
                          established_facts: (storyBible.established_facts || []).filter((_, i) => i !== index)
                        })}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Elaina was detained in an immigrant camp"
                      value={newFact}
                      onChange={(e) => setNewFact(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newFact.trim()) {
                          setStoryBible({ ...storyBible, established_facts: [...(storyBible.established_facts || []), newFact.trim()] });
                          setNewFact("");
                        }
                      }}
                    />
                    <Button variant="outline" onClick={() => {
                      if (newFact.trim()) {
                        setStoryBible({ ...storyBible, established_facts: [...(storyBible.established_facts || []), newFact.trim()] });
                        setNewFact("");
                      }
                    }}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Writing Rules Section */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">📝 Writing Rules <span className="text-sm font-normal text-gray-500">(Style and structure requirements)</span></h3>
                  <div className="space-y-2">
                    {(storyBible.writing_rules || []).map((rule, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                        <span className="flex-1 text-sm">{rule}</span>
                        <Button variant="ghost" size="sm" onClick={() => setStoryBible({
                          ...storyBible,
                          writing_rules: (storyBible.writing_rules || []).filter((_, i) => i !== index)
                        })}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Maintain political thriller tone"
                      value={newRule}
                      onChange={(e) => setNewRule(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newRule.trim()) {
                          setStoryBible({ ...storyBible, writing_rules: [...(storyBible.writing_rules || []), newRule.trim()] });
                          setNewRule("");
                        }
                      }}
                    />
                    <Button variant="outline" onClick={() => {
                      if (newRule.trim()) {
                        setStoryBible({ ...storyBible, writing_rules: [...(storyBible.writing_rules || []), newRule.trim()] });
                        setNewRule("");
                      }
                    }}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Forbidden Section */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">🚫 Forbidden <span className="text-sm font-normal text-gray-500">(Things AI must NOT do)</span></h3>
                  <div className="space-y-2">
                    {(storyBible.forbidden || []).map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-red-50 rounded">
                        <span className="flex-1 text-sm text-red-700">❌ {item}</span>
                        <Button variant="ghost" size="sm" onClick={() => setStoryBible({
                          ...storyBible,
                          forbidden: (storyBible.forbidden || []).filter((_, i) => i !== index)
                        })}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., Do not create new main characters"
                      value={newForbidden}
                      onChange={(e) => setNewForbidden(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newForbidden.trim()) {
                          setStoryBible({ ...storyBible, forbidden: [...(storyBible.forbidden || []), newForbidden.trim()] });
                          setNewForbidden("");
                        }
                      }}
                    />
                    <Button variant="outline" onClick={() => {
                      if (newForbidden.trim()) {
                        setStoryBible({ ...storyBible, forbidden: [...(storyBible.forbidden || []), newForbidden.trim()] });
                        setNewForbidden("");
                      }
                    }}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowStoryBible(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveStoryBible} 
                    disabled={savingBible}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {savingBible ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Save Story Bible
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default MultiBookSeries;
